import {
  GiftCardsGiftCard,
  OrdersDtoOrderLineDto,
  OrdersProductRequirementLineModel,
} from "@springtree/eva-services-core";
import { PrefigureDiscountsResponse } from "@springtree/eva-services-core/lib/eva-services-core";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect, useMemo } from "react";

import IconNonShoppableCountry from "@/icons/icon-non-shoppable-country";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useWishlist } from "~/contexts/wishlist";
import { useCartProduct } from "~/hooks/use-cart";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import CartProductPrice from "../../cart/cart-product-price";
import { CartProductTileContext } from "../../cart/cart-product-tile";
import styles from "../../cart/cart-product-tile.module.scss";
import Icon from "../../common/icon";
import { ProductActionOutcome } from "../../common/product-actions";
import QuantitySelector from "../../common/quantity-selector";
import SimpleQuantitySelector from "../../common/simple-quantity-selector";
import ProductInfoGift from "../../product/product-info-gift";
import GuestTrackYourOrderDiscountProductTile from "./guest-track-your-order-discount-product-tile";

type Props = {
  line: OrdersDtoOrderLineDto;
  disableCartActions?: boolean;
  index?: number;
  outOfStockDelivery?: boolean;
  outOfStockPickup?: boolean;
  onRemove?: (
    prodId: string,
    productName: string,
    index: number,
    quantity: number,
    outOfStockDelivery?: boolean
  ) => void;
  movedToWishlist?: (prodId: string, productName: string, index: number, quantity: number) => void;
  className?: string;
  disableCartQty?: boolean;
  quantity?: number;
  toggleQuantity?: (removing: boolean) => void;
  disableRemove?: boolean;
  disableAdd?: boolean;
  hideOrderQty?: boolean;
  onOpenLogin?: () => void;
  giftCardData?: GiftCardsGiftCard;
  physicalGiftCardRequirement?: OrdersProductRequirementLineModel[];
  context?: CartProductTileContext;
  trackingScope?: string;
  showNetPriceWithoutPromo?: boolean;
  guestPrefiguredDiscounts: PrefigureDiscountsResponse;
  rootProductDetails?: any;
  minicart?: boolean;
};

export default function GuestTrackYourOrderCartProductTile(props: Props) {
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const { isAuthenticated } = useAuthContext();
  const { saveForLater, refetch, savedForLaterList } = useWishlist();
  const { outOfStockDelivery = false, context = CartProductTileContext.default } = props;

  function _trackCartChange(trackingScope: string, action: ProductActionOutcome, line: OrdersDtoOrderLineDto) {
    const trackPayload = TrackingUtils.mapBasketAction(
      "cart",
      action,
      trackingScope,
      TrackingUtils.mapEcomCartItem(line)
    );
    trackPayload && TrackingUtils.track(trackPayload);
  }

  const existInSaveForLater = savedForLaterList?.Products?.find((el) => el.product_id === props.line.ProductID);
  const isGiftCard = productUtils.isGiftCard(props.line.Product?.Properties);

  const trackingInfo = useMemo(() => {
    if (!props.trackingScope) {
      return undefined;
    }
    return {
      context: props.trackingScope,
      info: TrackingUtils.mapEcomCartItem(props.line, props.rootProductDetails),
    };
  }, [props.line, props.rootProductDetails, props.trackingScope]);

  const { removeAllFromCart, addToCart, removeFromCart } = useCartProduct(
    props.line.ProductID!,
    isGiftCard ? props.line.ID : undefined,
    trackingInfo
  );

  const product = props.line.Product!.Properties as any;
  const primaryImage = product?.product_media?.primary_image;
  const displayName = productUtils.getShadeDisplayName(props.line.Product?.Properties);
  const goccia = productUtils.getProductDroplet(product?.product_media?.media);
  const productUrl = props.line.Product?.Properties?.slug
    ? props.line.Product?.Properties?.slug + "-" + props.line.ProductID
    : undefined;

  const saveForLaterHandler = async () => {
    if (isAuthenticated) {
      if (props.line.ProductID) {
        await removeAllFromCart.mutateAsync();
        await saveForLater(props.line.ProductID).then(() => refetch());
        if (props.movedToWishlist && props.index != undefined) {
          props.movedToWishlist(
            props.line.ProductID,
            product?.display_value || "",
            props.index,
            props.line.QuantityOpen || 0
          );
        }
      }
    } else if (props.onOpenLogin) {
      props.onOpenLogin();
    }
  };

  const removeHandler = async () => {
    if (props.line.ProductID) {
      await removeAllFromCart.mutateAsync();
      if (props.onRemove && props.index !== undefined && !isGiftCard) {
        props.onRemove(
          props.line.ProductID,
          product?.display_value,
          props.index,
          props.line.QuantityOpen || 0,
          outOfStockDelivery
        );
      }
    }
  };

  useEffect(() => {
    if (addToCart.isSuccess && props.trackingScope) {
      _trackCartChange(props.trackingScope, "added", props.line);
    }
  }, [addToCart.isSuccess, props.line, props.trackingScope]);

  useEffect(() => {
    if (removeFromCart.isSuccess && props.trackingScope) {
      _trackCartChange(props.trackingScope, "removed", props.line);
    }
  }, [removeFromCart.isSuccess, props.line, props.trackingScope]);

  const productLinkContent = (
    <>
      <Image
        src={props.giftCardData?.Data?.Theme?.ImageUrl || primaryImage?.url || imageUtils.transparentPixel}
        alt={product?.display_value || ""}
        width={80}
        height={80}
      />
      {outOfStockDelivery &&
        (context === CartProductTileContext.oosCartModal || context === CartProductTileContext.default) && (
          <div className={styles.outOfStockIconContainer}>
            <Icon icon={IconNonShoppableCountry} width={24} height={24} />
          </div>
        )}
    </>
  );

  return (
    <div className={styles.wrapperContainer}>
      <div
        className={classNames(
          styles.container,
          {
            [styles.cancelledItem]: props.line.IsCancelled,
            [styles.outOfStockDelivery]:
              outOfStockDelivery &&
              (context === CartProductTileContext.oosCartModal || context === CartProductTileContext.default),
          },
          props.className
        )}
      >
        {productUrl ? (
          <Link href={`/p/${productUrl}`} className={styles.productLink}>
            {productLinkContent}
          </Link>
        ) : (
          <div className={styles.productLink}>{productLinkContent}</div>
        )}
        <div className={styles.infoContainer}>
          <p className={styles.productName}>{product?.display_value || props.line.Product?.PrimitiveName}</p>
          {props.giftCardData || (props.physicalGiftCardRequirement || []).length > 0 ? (
            <ProductInfoGift
              giftCardData={props.giftCardData}
              physicalGiftCardRequirement={props.physicalGiftCardRequirement}
            />
          ) : displayName && !outOfStockDelivery ? (
            <div className={classNames(styles.colorContainer, { [styles.outOfStockDelivery]: outOfStockDelivery })}>
              <Image
                src={goccia?.url || imageUtils.transparentPixel}
                alt={displayName}
                width={12}
                height={12}
                className={classNames(styles.colorImage, { [styles.outOfStockDelivery]: outOfStockDelivery })}
                loader={imageUtils.evaImageLoader}
              />
              <p className={styles.colorName}>{displayName}</p>
            </div>
          ) : (
            <div className={styles.colorPlaceholder} />
          )}
          <div className={styles.bottomContainer}>
            <div className={styles.actionsContainer}>
              {context === CartProductTileContext.orderHistoryDetail && !outOfStockDelivery
                ? !props.hideOrderQty && (
                    <p className={styles.colorName}>
                      {t("generic.quantity", {
                        quantity:
                          props.context === CartProductTileContext.orderHistoryDetail
                            ? (props.line.QuantityOrdered || 0) - (props.line.QuantityCancelled || 0)
                            : props.line.QuantityOpen || 0,
                      })}
                    </p>
                  )
                : !props.line.CustomOrderLineType &&
                  !props.disableCartQty && (
                    <>
                      {!isGiftCard &&
                        !outOfStockDelivery &&
                        (props.toggleQuantity ? (
                          <SimpleQuantitySelector
                            quantity={props.quantity!}
                            onClick={props.toggleQuantity}
                            disableAdd={props.disableAdd}
                            disableRemove={props.disableRemove}
                          />
                        ) : (
                          <QuantitySelector
                            productID={props.line.ProductID!}
                            lineID={isGiftCard ? props.line.ID : undefined}
                            disableAdd={props.disableAdd}
                            disableRemove={props.disableRemove}
                            maxQuantity={props.line.Product?.Properties?.ecomm_max_items_per_order as number}
                            actionContext={props.minicart ? "minicart" : "cart"}
                            trackingData={trackingInfo?.info}
                          />
                        ))}
                      {!props.disableCartActions && breakpoint === "desktop" && (
                        <button
                          className={styles.button}
                          onClick={() => removeHandler()}
                          disabled={removeAllFromCart.isLoading}
                        >
                          {t("generic.remove")}
                        </button>
                      )}
                      {!props.disableCartActions && breakpoint === "desktop" && !existInSaveForLater && !isGiftCard && (
                        <button
                          className={styles.button}
                          onClick={() => saveForLaterHandler()}
                          disabled={removeAllFromCart.isLoading}
                        >
                          {t("generic.save_for_later")}
                        </button>
                      )}
                    </>
                  )}
            </div>
            <CartProductPrice showNetPriceWithoutPromo={props.showNetPriceWithoutPromo} line={props.line} />
          </div>
          {!props.showNetPriceWithoutPromo && props.line.Discounts?.length != 0 && (
            <GuestTrackYourOrderDiscountProductTile
              line={props.line}
              prefiguredDiscounts={props.guestPrefiguredDiscounts}
              showNetPriceWithoutPromo={props.showNetPriceWithoutPromo}
            />
          )}
          {!props.disableCartActions && !props.line.CustomOrderLineType && breakpoint === "mobile" && (
            <div className={styles.mobileActions}>
              <button className={styles.button} onClick={() => removeHandler()} disabled={removeAllFromCart.isLoading}>
                {t("generic.remove")}
              </button>
              {!existInSaveForLater && !isGiftCard && (
                <button
                  className={styles.button}
                  onClick={() => saveForLaterHandler()}
                  disabled={removeAllFromCart.isLoading}
                >
                  {t("generic.save_for_later")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {props.showNetPriceWithoutPromo && props.line.Discounts?.length != 0 && (
        <GuestTrackYourOrderDiscountProductTile
          line={props.line}
          prefiguredDiscounts={props.guestPrefiguredDiscounts}
          showNetPriceWithoutPromo={props.showNetPriceWithoutPromo}
        />
      )}
    </div>
  );
}
